import { isClientSubscribed } from '@frontend/api-client/api/client/utils';
import type { ClerkSSOLoginResponse, Client, ClientStatus, LoginResponse } from '@frontend/api-types';
import { useAuthSlice, useTrackingSlice } from '@frontend/redux';
import type { SSOContext } from '@frontend/web-react/components/SSO/types';
import { useSSO } from '@frontend/web-react/components/SSO/useSSO';
import { identifyClient } from '@frontend/web-utils/identify';
import { getRdtUuid } from '@frontend/web-utils/segment';
import { captureSentryException } from '@frontend/web-utils/sentry';
import {
  useClerkSSOLoginMutation,
  useFrictionlessSSOSignupMutation,
  useLazyListDraftTasksQuery,
  useLazyRetrieveClientQuery,
  useLazyRetrieveClientStatusQuery,
  useLoginMutation,
} from '@frontend/web/hooks/authApi';
import { router } from '@frontend/web/router';
import { handleMutation } from '@frontend/web/utils/api';
import { Env } from '@frontend/web/utils/env';
import { getPostLoginRedirect } from '@frontend/web/utils/routing/redirectPolicy';
import { useState } from 'react';
import { persistor } from '../redux';

type PostLoginSearch = {
  description?: string;
  promo?: string;
};

type LoginParams = {
  bypassInvalidate?: boolean;
  handleRedirect?: boolean;
  isSignupChangesEnabled?: boolean;
  onError?: (error: unknown) => void;
  onSuccess?: () => void;
  postLoginSearch?: PostLoginSearch;
  redirect?: string;
};

type SSOLoginParams = LoginParams & {
  conversionId?: UUID;
  inviteId?: string;
  promo?: string;
  rdtClickId?: string;
  referrer?: string;
  sessionId: string;
  twClickId?: string;
};

type AuthenticationSSOContext = Omit<SSOContext, 'destroyClient' | 'getToken' | 'signOut'> & {
  login: (params: SSOLoginParams) => Promise<unknown>;
};

const URL_PARAM_KEY = 'urlParamData';

export type URLParamData = {
  description?: string;
  guidedSignupSlidesViewed?: number;
  inviteId?: string;
  promo?: string;
  redirect?: string;
  referrer?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmMedium?: string;
  utmSource?: string;
  utmTerm?: string;
};

const getSessionStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage;
};

const normalizeURLParamData = (rawData: unknown): URLParamData | undefined => {
  if (rawData === null || typeof rawData !== 'object') {
    return undefined;
  }

  const parsedData = rawData as Record<string, unknown>;
  const description = typeof parsedData.description === 'string' ? parsedData.description : undefined;
  const guidedSignupSlidesViewed =
    typeof parsedData.guidedSignupSlidesViewed === 'number' ? parsedData.guidedSignupSlidesViewed : undefined;
  const inviteId = typeof parsedData.inviteId === 'string' ? parsedData.inviteId : undefined;
  const promo = typeof parsedData.promo === 'string' ? parsedData.promo : undefined;
  const redirect = typeof parsedData.redirect === 'string' ? parsedData.redirect : undefined;
  const referrer = typeof parsedData.referrer === 'string' ? parsedData.referrer : undefined;
  const utmCampaign = typeof parsedData.utmCampaign === 'string' ? parsedData.utmCampaign : undefined;
  const utmContent = typeof parsedData.utmContent === 'string' ? parsedData.utmContent : undefined;
  const utmMedium = typeof parsedData.utmMedium === 'string' ? parsedData.utmMedium : undefined;
  const utmSource = typeof parsedData.utmSource === 'string' ? parsedData.utmSource : undefined;
  const utmTerm = typeof parsedData.utmTerm === 'string' ? parsedData.utmTerm : undefined;

  if (
    !description &&
    !guidedSignupSlidesViewed &&
    !inviteId &&
    !promo &&
    !redirect &&
    !referrer &&
    !utmCampaign &&
    !utmContent &&
    !utmMedium &&
    !utmSource &&
    !utmTerm
  ) {
    return undefined;
  }

  return {
    ...(description ? { description } : {}),
    ...(guidedSignupSlidesViewed ? { guidedSignupSlidesViewed } : {}),
    ...(inviteId ? { inviteId } : {}),
    ...(promo ? { promo } : {}),
    ...(redirect ? { redirect } : {}),
    ...(referrer ? { referrer } : {}),
    ...(utmCampaign ? { utmCampaign } : {}),
    ...(utmContent ? { utmContent } : {}),
    ...(utmMedium ? { utmMedium } : {}),
    ...(utmSource ? { utmSource } : {}),
    ...(utmTerm ? { utmTerm } : {}),
  };
};

export const clearURLParamData = (): void => {
  getSessionStorage()?.removeItem(URL_PARAM_KEY);
};

export const getURLParamData = (): URLParamData | undefined => {
  const rawData = getSessionStorage()?.getItem(URL_PARAM_KEY);
  if (!rawData) {
    return undefined;
  }

  try {
    const normalizedData = normalizeURLParamData(JSON.parse(rawData));
    if (!normalizedData) {
      clearURLParamData();
      return undefined;
    }

    return normalizedData;
  } catch {
    clearURLParamData();
    return undefined;
  }
};

export const setURLParamData = (data: URLParamData): void => {
  if (
    !data.description &&
    !data.guidedSignupSlidesViewed &&
    !data.inviteId &&
    !data.promo &&
    !data.redirect &&
    !data.referrer &&
    !data.utmCampaign &&
    !data.utmContent &&
    !data.utmMedium &&
    !data.utmSource &&
    !data.utmTerm
  ) {
    clearURLParamData();
    return;
  }

  getSessionStorage()?.setItem(URL_PARAM_KEY, JSON.stringify(data));
};

export const useAuthentication = () => {
  const { clearAuth, storeToken, token } = useAuthSlice();
  const { fbclid, gclid } = useTrackingSlice();
  const {
    activeProvider,
    getToken,
    handleRedirectCallback,
    handleTransferToSignIn,
    handleTransferToSignUp,
    isLoaded: isSSOLoaded,
    isOAuthRedirecting,
    session,
    setActive,
    signIn,
    signOut,
    signUp,
    startOAuthFlow,
    updateSignUp,
  } = useSSO();

  const [clientStatus, setClientStatus] = useState<ClientStatus>();
  const [client, setClient] = useState<Client>();
  const [loginMutation, { isLoading: isLoginLoading }] = useLoginMutation();
  const [retrieveClientMutation] = useLazyRetrieveClientQuery();
  const [retrieveClientStatusMutation, { isLoading: isRetrieveClientStatusLoading }] =
    useLazyRetrieveClientStatusQuery();
  const [clerkSSOLoginMutation, { isLoading: isClerkSSOLoginLoading }] = useClerkSSOLoginMutation();
  const [frictionlessSSOSignupMutation, { isLoading: isFrictionlessSSOLoading }] = useFrictionlessSSOSignupMutation();
  const [listDraftTasks] = useLazyListDraftTasksQuery();

  const login = async ({
    bypassInvalidate,
    handleRedirect,
    isSignupChangesEnabled,
    mutationFn,
    onSuccess,
    postLoginSearch,
    redirect,
  }: LoginParams & { mutationFn: () => Promise<ClerkSSOLoginResponse | LoginResponse> }) =>
    handleMutation({
      mutation: () => mutationFn(),
      onSuccess: async ({ token, user: authenticatedClient }) => {
        setClient(authenticatedClient);
        storeToken(token);
        return handleMutation({
          errorMessage: 'Failed to retrieve client status. Please try again.',
          mutation: async () => {
            const shouldRetrieveClient = Boolean(handleRedirect && isSignupChangesEnabled);
            const [clientStatus, retrievedClient] = await Promise.all([
              retrieveClientStatusMutation().unwrap(),
              shouldRetrieveClient
                ? retrieveClientMutation()
                    .unwrap()
                    .catch(() => authenticatedClient)
                : Promise.resolve(authenticatedClient),
            ]);
            setClient(retrievedClient);
            setClientStatus(clientStatus);
            return {
              client: retrievedClient,
              clientStatus,
            };
          },
          onError: () => {
            logout();
          },
          onSuccess: async ({ client: resolvedClient, clientStatus }) => {
            await identifyClient(resolvedClient, Env.VITE_ENV, clientStatus, { fbclid, gclid });
            if (!bypassInvalidate) {
              router.invalidate();
            }
            if (handleRedirect) {
              const to = getPostLoginRedirect({
                client: resolvedClient,
                clientStatus,
                isSignupChangesEnabled: isSignupChangesEnabled ?? false,
                redirect,
              });
              return router.navigate({ ...(postLoginSearch ? { search: postLoginSearch } : {}), to });
            }
            onSuccess?.();
            return;
          },
          showSuccessToast: false,
        });
      },
      showSuccessToast: false,
    });

  const emailPasswordLogin = async ({
    bypassInvalidate,
    email,
    handleRedirect,
    isSignupChangesEnabled,
    onSuccess,
    password,
    redirect,
  }: LoginParams & {
    email: string;
    password: string;
  }) => {
    const trimmedEmail = email.trim();

    return login({
      bypassInvalidate,
      handleRedirect,
      isSignupChangesEnabled,
      mutationFn: () =>
        loginMutation({
          email: trimmedEmail,
          password,
        }).unwrap(),
      onSuccess,
      redirect,
    });
  };

  const ssoLogin: AuthenticationSSOContext['login'] = async ({
    bypassInvalidate,
    conversionId,
    handleRedirect,
    inviteId,
    isSignupChangesEnabled,
    onError,
    onSuccess,
    promo,
    rdtClickId,
    redirect,
    referrer,
    sessionId,
    twClickId,
  }) => {
    await setActive({ session: sessionId });
    const clerkToken = await getToken('backend-auth');
    if (!clerkToken) {
      throw new Error('No session token found');
    }
    const storedParams = getURLParamData();
    const isFrictionlessFlow = storedParams?.utmCampaign === 'try-first';
    const effectiveReferrer = referrer ?? storedParams?.referrer;
    const rdtUuid = getRdtUuid();
    const postLoginSearch =
      promo || storedParams?.description
        ? {
            ...(storedParams?.description ? { description: storedParams.description } : {}),
            ...(promo ? { promo } : {}),
          }
        : undefined;

    const mutationFn = isFrictionlessFlow
      ? () =>
          frictionlessSSOSignupMutation({
            clerkToken,
            ...(conversionId ? { conversionId } : {}),
            ...(storedParams?.description ? { taskDescription: storedParams.description } : {}),
          }).unwrap()
      : () =>
          clerkSSOLoginMutation({
            clerkToken,
            ...(conversionId ? { conversionId } : {}),
            ...(inviteId ? { inviteId } : {}),
            ...(promo ? { signupPromoCode: promo } : {}),
            ...(rdtClickId ? { rdtCid: rdtClickId } : {}),
            ...(rdtUuid ? { rdtUuid } : {}),
            ...(twClickId ? { twclid: twClickId } : {}),
            ...(effectiveReferrer ? { referredBy: effectiveReferrer } : {}),
          }).unwrap();
    return login({
      bypassInvalidate,
      handleRedirect: isFrictionlessFlow ? false : handleRedirect,
      isSignupChangesEnabled,
      mutationFn,
      onError,
      onSuccess: isFrictionlessFlow
        ? async () => {
            clearURLParamData();
            try {
              const draftTasks = await listDraftTasks().unwrap();
              const firstDraft = draftTasks[0];
              if (firstDraft) {
                router.navigate({ to: `/app/draft-task/${firstDraft.id}` });
                return;
              }
            } catch (error) {
              captureSentryException(error);
            }
            router.navigate({ to: redirect || '/app/home' });
          }
        : onSuccess,
      postLoginSearch,
      redirect,
    });
  };

  const logout = async () => {
    await signOut().catch(() => {
      // user may not be signed in - it's okay to fail silently here
      return null;
    });
    clearAuth();
    await persistor.flush();
    await router.invalidate();
    await router.navigate({ to: '/login' });
  };

  const isAnyLoading =
    isLoginLoading || isClerkSSOLoginLoading || isFrictionlessSSOLoading || isRetrieveClientStatusLoading;
  const isAuthenticated = !!token;

  const sso: AuthenticationSSOContext = {
    activeProvider,
    handleRedirectCallback,
    handleTransferToSignIn,
    handleTransferToSignUp,
    isLoaded: isSSOLoaded,
    isOAuthRedirecting,
    login: ssoLogin,
    session,
    setActive,
    signIn,
    signUp,
    startOAuthFlow,
    updateSignUp,
  };

  return {
    clearURLParamData,
    client,
    clientStatus,
    getURLParamData,
    isAuthenticated,
    isClientSubscribed: clientStatus && isClientSubscribed(clientStatus),
    isLoading: isAnyLoading,
    isLoginLoading: isLoginLoading || isRetrieveClientStatusLoading,
    login: emailPasswordLogin,
    logout,
    setURLParamData,
    sso,
  };
};

export type AuthContext = ReturnType<typeof useAuthentication>;
